"use client";
import { HomeFAQSection } from "@/app/components/FAQSection";
import { Box, Button, Typography } from "@mui/material";
import { useRouter } from "next/navigation";
import { useTheme } from "@mui/material/styles";
import { useLocale, useTranslations } from "next-intl";
import { getImageUrl, useResponsiveImageUrl } from "@/lib/getImageUrl";
import ModuleGrid from "./ModuleGrid";
import { useEffect } from "react";
import { getCourseCatalog, hasCompletedLesson } from "@/lib/courseData";
import { isLoggedIn } from "@/lib/hooks/useIsLoggedIn";

function JoinCitadelButton(props: { color?: "primary" | "tertiary" }) {
  const t = useTranslations("Citadel");
  const router = useRouter();

  const handleClick = async () => {
    if (!(await isLoggedIn())) {
      router.push("/auth/signup");
      return;
    }
    router.push("/products/citadel/course");
  };

  return (
    <Button
      variant="contained"
      onClick={handleClick}
      color={props.color || "tertiary"}
      sx={{
        fontWeight: 600,
        px: 4.5,
        py: 1.5,
      }}>
      {t("join")}
    </Button>
  );
}

export default function CitadelPage() {
  const theme = useTheme();
  const t = useTranslations("Citadel");
  const router = useRouter();
  const locale = useLocale();

  // 使用响应式图片 URL hook
  const eliteBackgroundImage = useResponsiveImageUrl("citadel/guide-elite.png", false, true);

  const modules = [
    {
      titleKey: "cryptoFoundations",
      imageUrl: getImageUrl("/citadel/guide-card1.png", theme.palette.mode),
    },
    {
      titleKey: "financialStats",
      imageUrl: getImageUrl("/citadel/guide-card2.png", theme.palette.mode),
    },
    {
      titleKey: "swingTrading",
      imageUrl: getImageUrl("/citadel/guide-card3.png", theme.palette.mode),
    },
    {
      titleKey: "buildSignals",
      imageUrl: getImageUrl("/citadel/guide-card4.png", theme.palette.mode),
    },
    {
      titleKey: "strategyDev",
      imageUrl: getImageUrl("/citadel/guide-card5.png", theme.palette.mode),
    },
    {
      titleKey: "huntingAlpha",
      imageUrl: getImageUrl("/citadel/guide-card6.png", theme.palette.mode),
    },
  ];

  const redirectToLesson = async () => {
    const catalogResult = await getCourseCatalog(locale);

    // 提取所有课程，避免重复 flatMap 操作
    const allLessons = catalogResult.data.flatMap((chapter: any) => chapter.lessons);

    // 优先选择第一个解锁、未完成的课程
    const firstIncomplete = allLessons.find((l: any) => !l.locked && !l.completed);
    if (firstIncomplete) {
      router.replace(`/products/citadel/course/${firstIncomplete.id}`);
      return;
    }

    // 否则选择最后一个完成的课程
    const lastCompleted = allLessons.findLast((l: any) => l.completed);
    if (lastCompleted) {
      router.replace(`/products/citadel/course/${lastCompleted.id}`);
      return;
    }
  };

  async function loadData() {
    const hasComp = await hasCompletedLesson();
    if (hasComp) {
      redirectToLesson();
    }
  }

  useEffect(() => {
    loadData();
  }, []);

  return (
    <Box
      sx={{
        bgcolor: "background.default",
        minHeight: "100vh",
        color: "text.primary",
        pb: "5rem",
      }}>
      {/* Hero */}
      <Box
        sx={{
          pt: "9.69rem",
          pb: "8.69rem",
          textAlign: "center",
          backgroundImage: `url(${getImageUrl("/citadel/guide-banner.jpg", theme.palette.mode)})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}>
        <Typography
          variant="h2"
          sx={{
            fontWeight: 600,
            fontSize: "var(--font-size-6xl)",
            lineHeight: 1.2,
            mb: 4,
            background:
              theme.palette.mode === "dark"
                ? "linear-gradient(90deg, var(--Color-Gold-100, #FFFAF5) 0%, #CC9E5A 100%)"
                : "linear-gradient(90deg, var(--Color-Gold-400, #CC9E5A) 0%, var(--Color-Gold-500, #A27D49) 100%)",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}>
          {t.rich("hero.title", {
            br: () => <br />,
          })}
        </Typography>
        <JoinCitadelButton color="primary" />
      </Box>
      <Box sx={{ maxWidth: "75rem", mx: "auto" }}>
        {/* Section 1 */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            mb: { xs: "5rem", md: "8rem" },
            gap: { xs: 4, md: 3 },
          }}>
          <Box sx={{ flex: 1, px: 3, py: 2, textAlign: { xs: "center", md: "left" } }}>
            <Typography
              sx={{
                color: "primary.light",
                fontSize: "var(--font-size-s)",
                fontWeight: 600,
                mb: 1,
              }}>
              {t("section1.subtitle")}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 600, fontSize: "var(--font-size-3xl)", mb: 2 }}>
              {t("section1.title")}
            </Typography>
            <Typography
              sx={{
                color: "text.secondary",
                mb: "2.5rem",
              }}>
              {t.rich("section1.desc", { br: () => <br /> })}
            </Typography>
            <JoinCitadelButton />
          </Box>
          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              maxWidth: { xs: "33.875rem", md: "100%" },
            }}>
            <img src={getImageUrl("citadel/guide-section1.png", theme.palette.mode)} style={{ width: "100%" }} />
          </Box>
        </Box>

        {/* Section 2 */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column-reverse", md: "row" },
            alignItems: "center",
            mb: { xs: "5rem", md: "8rem" },
            gap: { xs: 4, md: 3 },
          }}>
          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              maxWidth: { xs: "33.875rem", md: "100%" },
            }}>
            <img src={getImageUrl("citadel/guide-section2.png", theme.palette.mode)} style={{ width: "100%" }} />
          </Box>
          <Box sx={{ flex: 1, px: 3, py: 2, textAlign: { xs: "center", md: "right" } }}>
            <Typography variant="h4" sx={{ fontWeight: 600, fontSize: "var(--font-size-3xl)", mb: 3 }}>
              {t.rich("section2.title", { br: () => <br /> })}
            </Typography>
            <Typography
              sx={{
                color: "text.secondary",
                mb: 5,
              }}>
              {t.rich("section2.desc", { br: () => <br />, strong: (chunks) => <strong>{chunks}</strong> })}
            </Typography>
            <JoinCitadelButton />
          </Box>
        </Box>

        {/* What you'll learn */}
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, fontSize: "var(--font-size-3xl)", mb: 3 }}>
            {t("whatYouLearn.title")}
          </Typography>
          <Typography
            sx={{
              color: "text.secondary",
              mb: 5,
            }}>
            {t.rich("whatYouLearn.desc", { br: () => <br /> })}
          </Typography>
          <JoinCitadelButton />
        </Box>
        <ModuleGrid modules={modules} />

        {/* Real Wealth Building */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            mb: { xs: "5rem", md: "7.5rem" },
            gap: { xs: 3, md: 3 },
          }}>
          <Box sx={{ flex: 1, px: 3, py: 2, textAlign: { xs: "center", md: "left" } }}>
            <Typography variant="h5" sx={{ fontWeight: 600, fontSize: "var(--font-size-3xl)", mb: 3 }}>
              {t("realWealth.title")}
            </Typography>
            <Typography
              sx={{
                color: "text.secondary",
                mb: 5,
              }}>
              {t.rich("realWealth.desc", { br: () => <br /> })}
            </Typography>
            <JoinCitadelButton />
          </Box>
          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              maxWidth: { xs: "33.875rem", md: "100%" },
            }}>
            <img src={getImageUrl("citadel/guide-section3.png", theme.palette.mode)} style={{ width: "100%" }} />
          </Box>
        </Box>

        {/* The Elite Only */}
        <Box
          sx={{
            textAlign: "center",
            px: 3,
          }}>
          <Typography variant="h4" sx={{ fontWeight: 600, fontSize: "var(--font-size-3xl)", mb: 3 }}>
            {t("elite.title")}
          </Typography>
          <Typography
            sx={{
              color: "text.secondary",
              fontSize: "1rem",
              mb: 5,
            }}>
            {t.rich("elite.desc", { br: () => <br /> })}
          </Typography>
          <JoinCitadelButton color="primary" />

          {/* Background Image Box */}
          <Box
            sx={{
              mt: { xs: "1.4rem", md: "5.2rem" },
              mx: { xs: "-1.5rem", md: "-5rem" }, // 突破容器限制，左右拉伸
              height: "16.25rem", // 设置固定高度
              backgroundImage: `url(${eliteBackgroundImage})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
            }}></Box>
        </Box>
      </Box>
      <Box sx={{ mt: "1.7rem" }}>
        <HomeFAQSection domain="Citadel" itemCount={6} />
      </Box>
    </Box>
  );
}
